import { BOOKING_ATTEMPT_RESET } from 'actions/bookingAttempts/BookingAttemptsActionsTypes';
import { BOOKINGS_RESET } from 'actions/bookings/BookingsActionsTypes';
import {
    navigateToStep,
    removeOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import {
    ONLINE_BOOKING_CREATE_WAITLIST_START,
    ONLINE_BOOKING_CREATE_WAITLIST_SUCCESS,
    ONLINE_BOOKING_ERROR,
    ONLINE_BOOKING_SET_WAITLIST_CATEGORY,
    ONLINE_BOOKING_START_OVER,
    OnlineBookingAction
} from 'actions/onlineBooking/OnlineBookingActionsTypes';
import { createWaitlistThunk } from 'actions/waitlists/WaitlistsActions';
import { Moment } from 'moment-timezone';
import {
    filterAddonsByBusinessAreas,
    getActiveAddonServices
} from 'reducers/addonServices/AddonServicesSelectors';
import { getOnlineBookingWaitlistItems } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { ThunkAction } from 'redux-thunk';
import { RootState } from 'store';
import { RootAction } from 'types/Actions';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import {
    isOnlineBookingGroomingItem,
    OnlineBookingItem,
    OnlineBookingStep
} from 'types/OnlineBooking';
import { SaveWaitlistService, Waitlist, WaitlistTime } from 'types/Waitlist';
import {
    getWaitlistItemAddonTotal,
    getWaitlistItemDates,
    getWaitlistItemServiceTotal
} from 'utils/waitlistUtils';

export interface OnlineBookingWaitlistRequest {
    // ASAP: every service goes without dates (`[]`) instead of the ones picked in the cart
    asap: boolean;
    preferredTime: WaitlistTime | null;
    validUntil: Moment | null;
    notes: string;
}

// Informative snapshot with the cart pricing, as the total of the line (all its dates). The
// helpers return 0 when they can't resolve a price, which is sent as unknown (`null`).
const getWaitlistPrice = (price: number): number | null =>
    Number.isFinite(price) && price > 0 ? price : null;

/**
 * Waitlist services for the cart items: the dates picked in the cart (or `[]` for ASAP)
 * and the line totals for those dates. Only grooming lets the customer pick a staff (one
 * for the whole cart), so it is repeated on every grooming item; the rest go with
 * "No preference" (`null`).
 */
export const buildOnlineBookingWaitlistServices = (
    items: Array<OnlineBookingItem>,
    asap: boolean
): Array<SaveWaitlistService> => {
    const waitlistItems = items.filter(item => item.service?.id);

    const groomingStaffId =
        waitlistItems.filter(isOnlineBookingGroomingItem).find(item => item.preferredStaff)
            ?.preferredStaff?.id ?? null;

    return waitlistItems.map(item => ({
        petId: item.pet.id,
        serviceId: Number(item.service?.id),
        price: getWaitlistPrice(getWaitlistItemServiceTotal(item, asap)),
        preferredStaffId: isOnlineBookingGroomingItem(item) ? groomingStaffId : null,
        dates: asap ? [] : getWaitlistItemDates(item),
        addons: item.addons.map(addon => ({
            addonServiceId: addon.id,
            price: getWaitlistPrice(getWaitlistItemAddonTotal(item, addon, asap))
        }))
    }));
};

export const setOnlineBookingWaitlistCategory = (
    category?: MarketplaceServiceCategory
): OnlineBookingAction => ({
    type: ONLINE_BOOKING_SET_WAITLIST_CATEGORY,
    payload: { category }
});

/**
 * Entry point of the "Join Waitlist" CTA in the date step of each business area.
 *
 * Boarding add-ons are normally picked after the dates, so when the boarding area
 * has add-ons the customer goes through the add-ons step first (in waitlist mode).
 */
export const startOnlineBookingWaitlistThunk = (
    category: MarketplaceServiceCategory
): ThunkAction<void, RootState, null, RootAction> => {
    return (dispatch, getState) => {
        dispatch(setOnlineBookingWaitlistCategory(category));

        if (category === MarketplaceServiceCategory.BOARDING) {
            const boardingAddons = filterAddonsByBusinessAreas(getActiveAddonServices(getState()), [
                MarketplaceServiceCategory.BOARDING
            ]);

            if (boardingAddons.length > 0) {
                dispatch(navigateToStep(OnlineBookingStep.AddonList));
                return;
            }
        }

        dispatch(navigateToStep(OnlineBookingStep.JoinWaitlist));
    };
};

export const createOnlineBookingWaitlistThunk = (
    request: OnlineBookingWaitlistRequest
): ThunkAction<Promise<Waitlist>, RootState, null, RootAction> => {
    return async (dispatch, getState) => {
        try {
            dispatch({ type: ONLINE_BOOKING_CREATE_WAITLIST_START });

            const state = getState();

            const waitlist = await dispatch(
                createWaitlistThunk({
                    marketplaceId: state.marketplace.data.id,
                    preferredTime: request.preferredTime,
                    validUntil: request.validUntil,
                    notes: request.notes,
                    services: buildOnlineBookingWaitlistServices(
                        getOnlineBookingWaitlistItems(state),
                        request.asap
                    )
                })
            );

            dispatch({ type: ONLINE_BOOKING_CREATE_WAITLIST_SUCCESS });

            // The backend closes the customer's open booking attempts for this location,
            // so the next step navigation must start a new attempt session.
            dispatch({ type: BOOKING_ATTEMPT_RESET });

            return waitlist;
        } catch (error) {
            dispatch({ type: ONLINE_BOOKING_ERROR });

            throw error;
        }
    };
};

/**
 * Clears the cart and starts a new booking session for the same, still
 * authenticated, customer.
 */
export const startOverOnlineBookingThunk = (): ThunkAction<void, RootState, null, RootAction> => {
    return dispatch => {
        dispatch({ type: ONLINE_BOOKING_START_OVER });
        dispatch({ type: BOOKINGS_RESET });
        dispatch({ type: BOOKING_ATTEMPT_RESET });

        dispatch(navigateToStep(OnlineBookingStep.PetList));
    };
};

/**
 * "Go Back to Online Booking" from the waitlist success screen: the waitlisted
 * items leave the cart; the customer continues with whatever is left, or starts over.
 */
export const leaveOnlineBookingWaitlistThunk = (): ThunkAction<
    void,
    RootState,
    null,
    RootAction
> => {
    return (dispatch, getState) => {
        const state = getState();

        const waitlistUuids = getOnlineBookingWaitlistItems(state).map(item => item.uuid);
        const hasRemainingItems = state.onlineBooking.bookingItems.some(
            item => !waitlistUuids.includes(item.uuid)
        );

        if (!hasRemainingItems) {
            dispatch(startOverOnlineBookingThunk());
            return;
        }

        dispatch(removeOnlineBookingBookingItems(waitlistUuids));
        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    };
};
